function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { useEffect, useRef, useCallback, useReducer, useState, useContext } from 'react';
import Sortable from 'sortablejs';
import { useSelector, useDispatch } from 'react-redux';
import shallowEqual from 'shallowequal';
import { useTranslation } from 'react-i18next';
import { ActionButton, TitleComponent, createTree, MousePositionedTooltip } from '@keen.io/ui-core';
import { useSearch } from '@keen.io/react-hooks';
import { colors } from '@keen.io/colors';
import { BodyText } from '@keen.io/typography';
import { Section, GroupSettings, SortableContainer } from './GroupBy.styles';
import { addGroup, setGroups, removeGroup, resetGroups, selectGroupProperty } from './actions';
import { groupByReducer } from './reducer';
import { convertGroups, serializeGroups } from './utils';
import { DRAG_ANIMATION_TIME } from './constants';
import SearchableProperty from '../SearchableProperty';
import { AppContext, SearchContext } from '../../contexts';
import { mutateArray } from '../../utils';
import { getEventCollection, getGroupBy, setGroupBy } from '../../modules/query';
import { getCollectionSchema } from '../../modules/events';

var GroupBy = function GroupBy(_ref) {
  var collection = _ref.collection;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useContext = useContext(AppContext),
      modalContainer = _useContext.modalContainer;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      propertiesTree = _useState2[0],
      setPropertiesTree = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      searchPropertiesPhrase = _useState4[0],
      setSearchPhrase = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      expandTree = _useState6[0],
      setTreeExpand = _useState6[1];

  var dispatch = useDispatch();
  var groups = useSelector(function (state) {
    var groupBy = getGroupBy(state);

    if (groupBy) {
      if (Array.isArray(groupBy)) return groupBy;
      if (typeof groupBy === 'string') return [groupBy];
    }

    return [];
  }, shallowEqual);
  var groupsRef = useRef(groups);
  var eventCollection = useSelector(getEventCollection);

  var _useSelector = useSelector(function (state) {
    return getCollectionSchema(state, collection);
  }),
      schemaTree = _useSelector.tree,
      schemaList = _useSelector.list;

  var _useReducer = useReducer(groupByReducer, convertGroups(groups)),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      groupDispatcher = _useReducer2[1];

  var stateRef = useRef(state);

  var _useSearch = useSearch(schemaList, function (searchResult, phrase) {
    if (phrase) {
      var searchTree = {};
      searchResult.forEach(function (_ref2) {
        var path = _ref2.path,
            type = _ref2.type;
        searchTree[path] = type;
      });
      setSearchPhrase(phrase);
      setPropertiesTree(createTree(searchTree));
      setTreeExpand(true);
    } else {
      setTreeExpand(false);
      setPropertiesTree(null);
    }
  }, {
    keys: ['path', 'type'],
    threshold: 0.4
  }),
      searchHandler = _useSearch.searchHandler;

  var clearSearchHandler = useCallback(function () {
    setPropertiesTree(null);
    setSearchPhrase(null);
  }, []);
  useEffect(function () {
    return function () {
      dispatch(setGroupBy(undefined));
    };
  }, []);
  useEffect(function () {
    var localGroups = serializeGroups(state);

    if (!shallowEqual(groups, groupsRef.current) && !shallowEqual(groups, localGroups)) {
      if (groups) {
        groupDispatcher(setGroups(convertGroups(groups)));
      } else {
        groupDispatcher(resetGroups());
      }
    }

    groupsRef.current = groups;
  }, [groups]);
  useEffect(function () {
    if (!shallowEqual(state, stateRef.current)) {
      stateRef.current = state;

      var _groups = serializeGroups(state);

      var updatedGroups = _groups.length ? _groups : undefined;
      dispatch(setGroupBy(updatedGroups));
    }
  }, [state]);
  var sortableRef = useRef(null);

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isDragged = _useState8[0],
      setDragMode = _useState8[1];

  useEffect(function () {
    var dragGhost;
    new Sortable(sortableRef.current, {
      animation: DRAG_ANIMATION_TIME,
      filter: '.add-button',
      handle: '.drag-handle',
      onStart: function onStart() {
        setDragMode(true);
        setTreeExpand(false);
      },
      onMove: function onMove(evt) {
        return !evt.related.className.includes('add-button');
      },
      onEnd: function onEnd(evt) {
        var updatedGroups = mutateArray(stateRef.current, evt.oldIndex, evt.newIndex);
        groupDispatcher(setGroups(updatedGroups));
        setDragMode(false);
        if (dragGhost) dragGhost.parentNode.removeChild(dragGhost);
      },
      setData: function setData(dataTransfer, dragEl) {
        dragGhost = dragEl.cloneNode(true);
        var styles = {
          width: dragEl.offsetWidth,
          transform: 'translateX(-100%)',
          position: 'absolute'
        };
        Object.assign(dragGhost.style, styles);
        var tree = dragGhost.querySelector('[data-testid="properties-tree"]');
        if (tree) tree.remove();
        document.body.appendChild(dragGhost);
        dataTransfer.setDragImage(dragGhost, 10, 10);
      }
    });
  }, []);

  var selectEventStreamTooltip = function selectEventStreamTooltip() {
    return React.createElement(BodyText, {
      variant: "body2",
      fontWeight: "normal",
      "data-testid": "select-event-stream",
      color: colors.white[500]
    }, t('query_creator_group_by.select'), ' ', React.createElement("strong", null, t('query_creator_group_by.event_stream')), ' ', t('query_creator_group_by.tooltip'));
  };

  return React.createElement(React.Fragment, null, React.createElement(TitleComponent, {
    isDisabled: !eventCollection
  }, t('query_creator_group_by.title')), React.createElement(Section, null, React.createElement(SearchContext.Provider, {
    value: {
      expandTree: expandTree,
      searchPropertiesPhrase: searchPropertiesPhrase
    }
  }, React.createElement(MousePositionedTooltip, {
    isActive: !eventCollection,
    tooltipTheme: "dark",
    tooltipPortal: modalContainer,
    renderContent: selectEventStreamTooltip
  }, React.createElement(SortableContainer, {
    ref: sortableRef,
    "data-testid": "group-by-wrapper"
  }, state.map(function (_ref3) {
    var property = _ref3.property,
        id = _ref3.id;
    return React.createElement(GroupSettings, {
      "data-testid": "groupBy-settings-item",
      key: id
    }, React.createElement(SearchableProperty, {
      isEditAllowed: !isDragged,
      properties: propertiesTree ? propertiesTree : schemaTree,
      property: property,
      onSearchProperties: searchHandler,
      onSelectProperty: function onSelectProperty(property) {
        clearSearchHandler();
        groupDispatcher(selectGroupProperty(id, property));
      },
      onRemove: function onRemove() {
        clearSearchHandler();
        groupDispatcher(removeGroup(id));
      },
      onBlur: function onBlur() {
        if (!property) groupDispatcher(removeGroup(id));
      }
    }));
  }), React.createElement(ActionButton, {
    className: "add-button",
    isDisabled: !eventCollection,
    action: "create",
    onClick: function onClick() {
      return groupDispatcher(addGroup(''));
    }
  }))))));
};

export default GroupBy;